/* The triggers that can force an automatic reload, each guarded independently. */
type AutoReloadTrigger = "new-version" | "preload-error";

/*
 * Each trigger records the build id it last reloaded for in sessionStorage —
 * which survives reloads but not tabs — so a reload that fails to deliver a
 * different build (e.g. a CDN still propagating a deploy) cannot loop.
 */
const STORAGE_KEY_PREFIX = "df-auto-refresh";

const storageKey = (trigger: AutoReloadTrigger): string => `${STORAGE_KEY_PREFIX}:${trigger}`;

/**
 * shouldAutoReload function
 * Determines whether an automatic reload is allowed for the given trigger —
 * true unless this trigger has already reloaded this tab for the same build id.
 *
 * @param trigger - The auto-reload trigger ("new-version" or "preload-error")
 * @param buildId - The build id the reload is being attempted for
 * @returns true when a reload has not already been attempted for this build id
 */
export const shouldAutoReload = (trigger: AutoReloadTrigger, buildId: string): boolean => {
  try {
    return sessionStorage.getItem(storageKey(trigger)) !== buildId;
  } catch {
    // sessionStorage unavailable — allow the reload (matches unguarded behaviour).
    return true;
  }
};

/**
 * trackAutoReload function
 * Records that an automatic reload was attempted for the given trigger and
 * build id, so shouldAutoReload can stop the same reload from repeating.
 *
 * @param trigger - The auto-reload trigger ("new-version" or "preload-error")
 * @param buildId - The build id the reload was attempted for
 * @returns void
 */
export const trackAutoReload = (trigger: AutoReloadTrigger, buildId: string): void => {
  try {
    sessionStorage.setItem(storageKey(trigger), buildId);
  } catch {
    // sessionStorage unavailable — nothing to record.
  }
};
